import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import dayjs from 'dayjs';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { vocabularyTestActions } from 'pages/VocabularyTest/store/vocabularyTest.slice';
import TestInProgress from 'pages/VocabularyTest/components/TestInProgress';
import { fillUrlIds, pagePaths } from 'routes/paths';

/**
 * URL-addressable wrapper for the active testing UI. Lives at
 * {@code /test-my-vocabulary/run} so the test screen survives page refreshes
 * and browser back/forward navigation.
 *
 * <p>Responsibilities:
 * <ul>
 *   <li>Bootstrap the history fetch (so {@link TestInProgress} has data).
 *   <li>Auto-redirect to the result page when the user finishes the test.
 *   <li>Auto-redirect back to the landing when no IN_PROGRESS test exists
 *       (e.g. deep-link to {@code /run} after deletion).
 * </ul>
 */
const VocabularyTestRunner: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    data: history,
    loading: historyLoading,
  } = useAppSelector((store) => store.vocabularyTest.vocabularyTestHistory);
  const { loading: inProgressLoading, data: inProgressBlocks } = useAppSelector(
    (store) => store.vocabularyTest.vocabularyTestInProgress,
  );
  const { completedTestResult } = useAppSelector(
    (store) => store.vocabularyTest,
  );

  const inProgressTest = history.find(
    (test) => test.status === 'IN_PROGRESS',
  );
  const hasInProgressTest = !!inProgressTest;
  // Label the active run in the breadcrumb by its start moment, e.g.
  // "Тест 04.07.2026 18:30". Empty until the IN_PROGRESS history row loads —
  // BreadCrumbs drops empty extra items, so no placeholder crumb flashes.
  const runCrumbLabel = inProgressTest
    ? `Тест ${dayjs(inProgressTest.createdAt).format('DD.MM.YYYY HH:mm')}`
    : '';
  // `startTesting` POSTs `/current` first and the IN_PROGRESS row only shows
  // up in `history` after a follow-up GET. Between those two requests
  // `vocabularyTestInProgressSet` resets `historyLoading` to false (slice
  // side-effect), which would let the bounce-back effect fire on stale
  // history. Treat freshly-fetched blocks as proof an active test exists.
  const hasInProgressBlocks = inProgressBlocks.length > 0;

  useEffect(() => {
    dispatch(vocabularyTestActions.vocabularyTestHistoryGet({ page: 0 }));
  }, [dispatch]);

  // Pull the in-progress test's blocks explicitly. We only fetch when there
  // is genuinely an IN_PROGRESS row in history and the blocks slot is empty
  // and idle. The startTesting flow flips inProgress.loading=true via its
  // reducer the moment the user clicks, so this effect sits out until the
  // saga-driven POST /current lands — no duplicate request.
  useEffect(() => {
    if (historyLoading) return;
    if (!hasInProgressTest) return;
    if (hasInProgressBlocks) return;
    if (inProgressLoading) return;
    dispatch(vocabularyTestActions.vocabularyTestInProgressGet());
  }, [
    dispatch,
    historyLoading,
    hasInProgressTest,
    hasInProgressBlocks,
    inProgressLoading,
  ]);

  // Bounce back to the landing if there is genuinely no in-progress test —
  // someone landed here via a stale bookmark or after deleting the test in
  // another tab. We wait until history has loaded at least once before deciding.
  useEffect(() => {
    if (historyLoading) return;
    if (history.length === 0) return; // initial load not finished
    if (hasInProgressTest) return;
    // Just-started test: POST /current has populated blocks but the history
    // refetch hasn't landed yet. Don't bounce — the IN_PROGRESS row will arrive.
    if (hasInProgressBlocks) return;
    navigate('/' + pagePaths.vocabularyTest, { replace: true });
  }, [
    hasInProgressTest,
    hasInProgressBlocks,
    history.length,
    historyLoading,
    navigate,
  ]);

  // When the test completes, go to its result page (replaces the /run entry so
  // browser back doesn't bring the user back into the just-finished test).
  useEffect(() => {
    if (completedTestResult) {
      navigate(
        fillUrlIds(pagePaths.vocabularyTestResult, {
          vocabularyTestResultId: String(completedTestResult.id),
        }),
        { replace: true },
      );
      dispatch(vocabularyTestActions.completedTestResultClose());
    }
  }, [completedTestResult, dispatch, navigate]);

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
      <UIContainer>
        <BreadCrumbs
          extraItems={runCrumbLabel ? [{ label: runCrumbLabel }] : []}
        />
      </UIContainer>

      <Stack direction="column" mt={2} minHeight={200}>
        <WithLoader loading={inProgressLoading || historyLoading}>
          <TestInProgress />
        </WithLoader>
      </Stack>
    </Box>
  );
};

export default VocabularyTestRunner;
